import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Input from "components/form/input/Input";
import Popup from "components/popup/Popup";
import Sheet from "components/sheet/Sheet";
import {
  PostEmailMarketingTemplateBlank,
  PutEmailMarketingTemplateName,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplates";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import {
  EMAIL_BUILDER_TEMPLATE_NAME_MAX_LENGTH,
  validateEmailBuilderTemplateName,
} from "../saveAsTemplate/emailBuilderSaveAsTemplateName";
import "./emailBuilderTemplateLibrary.scss";
import { StatusCode } from "api/protocols";

export type TEmailBuilderTemplateRenameTarget = {
  id: number;
  name: string;
};

type TEmailBuilderTemplateLibraryNameModalProps =
  | {
      mode: "create";
      open: boolean;
      onClose: () => void;
      onCreated: (templateId: number) => void;
    }
  | {
      mode: "rename";
      open: boolean;
      template: TEmailBuilderTemplateRenameTarget | null;
      onClose: () => void;
      onRenamed: (template: TEmailBuilderTemplateRenameTarget) => void;
    };

function apiErrorMessage(message: unknown, fallback: string): string {
  if (typeof message === "string" && message.length > 0) {
    return message;
  }

  return fallback;
}

type TEmailBuilderTemplateNameFieldProps = {
  name: string;
  validationError: string | null;
  saving: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onNameChange: (value: string) => void;
  onSubmit: () => void;
};

function EmailBuilderTemplateNameField(props: TEmailBuilderTemplateNameFieldProps) {
  const { t } = useTranslation();

  return (
    <div className="email-builder-template-library-rename-field">
      <Input
        label={t("Template name")}
        placeholder={t("e.g. Monthly Newsletter Layout")}
        value={props.name}
        maxLength={EMAIL_BUILDER_TEMPLATE_NAME_MAX_LENGTH}
        error={props.validationError !== null}
        disabled={props.saving}
        inputRef={props.inputRef}
        onChange={event => {
          props.onNameChange(event.target.value);
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.key === "Enter") {
            event.preventDefault();
            props.onSubmit();
          }
        }}
      />
      {props.validationError !== null ? (
        <div className="email-builder-template-library-rename-error" role="alert">
          {props.validationError}
        </div>
      ) : null}
    </div>
  );
}

export function EmailBuilderTemplateLibraryNameModal(
  props: TEmailBuilderTemplateLibraryNameModalProps,
) {
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const isMobile = (windowSize.width ?? MOBILE_WIDTH + 1) <= MOBILE_WIDTH;
  const [name, setName] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isRename = props.mode === "rename";
  const renameTemplate = isRename ? props.template : null;
  const isReady = props.open && (!isRename || renameTemplate !== null);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const active = window.document.activeElement;
    returnFocusRef.current = active instanceof HTMLElement ? active : null;
    setName(isRename && renameTemplate !== null ? renameTemplate.name : "");
    setValidationError(null);
    setSaving(false);

    const focusTimeoutId = window.setTimeout(() => {
      inputRef.current?.focus();
      if (isRename) {
        inputRef.current?.select();
      }
    }, 50);

    return () => {
      window.clearTimeout(focusTimeoutId);
    };
  }, [props.open, props.mode, isRename, isReady, renameTemplate]);

  useEffect(() => {
    if (props.open) {
      return;
    }

    const returnFocus = returnFocusRef.current;
    if (returnFocus !== null && typeof returnFocus.focus === "function") {
      returnFocus.focus();
    }
    returnFocusRef.current = null;
  }, [props.open]);

  useEffect(() => {
    if (!props.open || saving || !isRename) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        props.onClose();
      }
    }

    window.document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.open, props.onClose, saving, isRename]);

  function handleClose() {
    if (saving) {
      return;
    }

    props.onClose();
  }

  function clearValidationOnChange(nextName: string) {
    setName(nextName);
    if (validationError !== null) {
      setValidationError(null);
    }
  }

  async function handleSubmit() {
    if (saving) {
      return;
    }

    const validated = validateEmailBuilderTemplateName(name);
    if (validated.ok === false) {
      setValidationError(t(validated.error));
      return;
    }

    if (props.mode === "rename") {
      if (renameTemplate === null) {
        return;
      }

      if (validated.name === renameTemplate.name) {
        props.onClose();
        return;
      }

      setValidationError(null);
      setSaving(true);

      try {
        const res = await PutEmailMarketingTemplateName(
          renameTemplate.id,
          { name: validated.name },
          false,
        );

        if (res.status !== StatusCode.OK || res.data === null) {
          setValidationError(apiErrorMessage(res.message, t("Could not rename template.")));
          return;
        }

        props.onRenamed({
          id: renameTemplate.id,
          name: res.data.name,
        });
        props.onClose();
      } finally {
        setSaving(false);
      }

      return;
    }

    setValidationError(null);
    setSaving(true);

    try {
      const res = await PostEmailMarketingTemplateBlank({ name: validated.name }, true);

      if (res.status !== StatusCode.OK || res.data?.data == null) {
        setValidationError(apiErrorMessage(res.message, t("Could not create template.")));
        return;
      }

      props.onCreated(res.data.data.id);
      props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const title = isRename ? t("Rename template") : t("Create template");
  const okText = saving
    ? isRename
      ? t("Saving…")
      : t("Creating…")
    : isRename
      ? t("Save")
      : t("Create");
  const submitDisabled = saving || (isRename && renameTemplate === null);

  const field = (
    <EmailBuilderTemplateNameField
      name={name}
      validationError={validationError}
      saving={saving}
      inputRef={inputRef}
      onNameChange={clearValidationOnChange}
      onSubmit={() => {
        void handleSubmit();
      }}
    />
  );

  if (isMobile) {
    return (
      <Sheet
        open={props.open}
        size="small"
        title={title}
        closable
        onCancel={handleClose}
        cancelText={t("Cancel")}
        okText={isRename ? t("Save") : t("Create")}
        onOk={() => {
          void handleSubmit();
        }}
        okLoading={saving}
        okDisabled={submitDisabled}
        cancelDisabled={saving}
        backDropCancel={!saving}
      >
        {field}
      </Sheet>
    );
  }

  return (
    <Popup
      open={props.open}
      type="info"
      title={title}
      closable
      onCancel={handleClose}
      cancelText={t("Cancel")}
      okText={okText}
      onOk={() => {
        void handleSubmit();
      }}
      okDisabled={submitDisabled}
      cancelDisabled={saving}
      backDropCancel={!saving}
    >
      {field}
    </Popup>
  );
}

export function EmailBuilderTemplateLibraryRenameModal(
  props: {
    open: boolean;
    template: TEmailBuilderTemplateRenameTarget | null;
    onClose: () => void;
    onRenamed: (template: TEmailBuilderTemplateRenameTarget) => void;
  },
) {
  return (
    <EmailBuilderTemplateLibraryNameModal
      mode="rename"
      open={props.open}
      template={props.template}
      onClose={props.onClose}
      onRenamed={props.onRenamed}
    />
  );
}
